import { Stack, Typography } from '@mui/material';
import { compact, isEmpty } from 'lodash';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { LinkTabProps } from '@/components/navigation/NavigationTabs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useMatchingRoutes } from '@/hooks/useMatchingRoutes';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AISuggestionsV2Banner } from '@/modules/aiSuggestions/components/AISuggestionsV2Banner/AISuggestionsV2Banner';
import { ReviewSuggestionsTrowserMount } from '@/modules/aiSuggestions/reviewSuggestions/ReviewSuggestionsTrowserMount';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { DocumentUploadButton } from '@/modules/documents/components/DocumentUploadButton/DocumentUploadButton';
import { EstateCreateButton } from '@/modules/estate/components/EstateCreateButton';
import { EstateHoldingsProvider } from '@/modules/estate/contexts/EstateHoldings.provider';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingUnacknowledgedSuggestionsProvider } from '@/modules/estate/estateAIOnboarding/context/EstateOnboardingUnacknowledgedSuggestions.provider';
import { EstateOnboardingSuggestionGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { EstateOnboardingSuggestionsBanner } from '@/modules/estate/estateAIOnboarding/EstateOnboardingSuggestionsBanner';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { DataLinkButton } from './DataLinkButton';

export function ClientEstatePage() {
  const householdId = useRequiredParam('householdId');

  if (!householdId) {
    return null;
  }

  return (
    <EstateHoldingsProvider>
      <EstateOnboardingUnacknowledgedSuggestionsProvider>
        <ClientEstatePageInner />
      </EstateOnboardingUnacknowledgedSuggestionsProvider>
    </EstateHoldingsProvider>
  );
}

export function ClientEstatePageInner() {
  const householdId = useRequiredParam('householdId');
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();
  const { canConnectToIntegrations } = useIsAssetProviderIntegrationEnabled();
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const { countsByGroup } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();

  const { isMatchingRoute: isOnV2BannerTab } = useMatchingRoutes([
    ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS,
    ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS,
    ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES,
  ]);

  const tabs: LinkTabProps[] = useMemo(
    () => createEstateTabs(householdId, countsByGroup, canEdit),
    [householdId, countsByGroup, canEdit]
  );

  const action = useMemo(() => {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        {canEdit && (
          <DocumentUploadButton
            householdId={householdId}
            variant="secondary"
            documentText="Upload documents"
            icon={aiCapabilitiesEnabled ? Stars01Icon : Upload01Icon}
            additionalUploadDetails={
              <Typography variant="body-s" mb={2}>
                Upload documents such as spreadsheets, insurance policies,
                account statements, etc. Luminary AI will extract
                suggestions—holdings, trusts, businesses—for your review
              </Typography>
            }
          />
        )}
        {canManageIntegrationData && !isEmpty(canConnectToIntegrations) && (
          <DataLinkButton />
        )}
        {canEdit && <EstateCreateButton householdId={householdId} />}
      </Stack>
    );
  }, [
    canEdit,
    canManageIntegrationData,
    householdId,
    aiCapabilitiesEnabled,
    canConnectToIntegrations,
  ]);

  return (
    <>
      {/* minHeight lets the content region fill the viewport-tall `page` box so
          tabs whose content grows (via flex) can center within the leftover space. */}
      <Stack sx={{ minHeight: '100%' }}>
        <PageHeader heading="Estate" tabs={tabs} action={action} />
        <Stack p={2} spacing={2} flex={1}>
          {canEdit && <EstateOnboardingSuggestionsBanner />}
          {canEdit && isOnV2BannerTab && <AISuggestionsV2Banner />}
          <Outlet />
        </Stack>
      </Stack>
      {canEdit && <ReviewSuggestionsTrowserMount />}
    </>
  );
}

function createEstateTabs(
  householdId: string,
  countsByGroup: Record<EstateOnboardingSuggestionGroup, number> | undefined,
  canEdit: boolean
): LinkTabProps[] {
  const withOptionalBadge = (label: string, count?: number) => {
    if (typeof count === 'number' && count > 0) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body-xs" color="inherit">
            {label}
          </Typography>
          {canEdit && (
            <Badge
              variant={BadgeVariants.Success}
              display={count}
              wrapperSx={{
                minWidth: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            />
          )}
        </Stack>
      );
    }
    return label;
  };

  const directHoldingsCount =
    countsByGroup?.[EstateOnboardingSuggestionGroup.DirectHoldings] || 0;
  const trustCount =
    (countsByGroup?.[EstateOnboardingSuggestionGroup.TrustEntities] || 0) +
    (countsByGroup?.[EstateOnboardingSuggestionGroup.TrustHoldings] || 0);
  const businessCount =
    (countsByGroup?.[EstateOnboardingSuggestionGroup.BusinessEntities] || 0) +
    (countsByGroup?.[EstateOnboardingSuggestionGroup.BusinessHoldings] || 0);

  return compact([
    {
      display: 'Balance sheet',
      dataTestId: 'tab-HOUSEHOLD_ESTATE_BALANCE_SHEET',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_BALANCE_SHEET,
        {
          householdId,
        }
      ),
    },
    {
      display: withOptionalBadge(
        'Direct holdings',
        directHoldingsCount || undefined
      ),
      dataTestId: 'tab-HOUSEHOLD_ESTATE_DIRECT_HOLDINGS',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS,
        { householdId }
      ),
    },
    {
      display: withOptionalBadge('Trusts', trustCount || undefined),
      dataTestId: 'tab-HOUSEHOLD_ESTATE_TRUSTS',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, {
        householdId,
      }),
    },
    {
      display: withOptionalBadge('Businesses', businessCount || undefined),
      dataTestId: 'tab-HOUSEHOLD_ESTATE_BUSINESSES',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES, {
        householdId,
      }),
    },
    {
      display: 'Life insurance',
      dataTestId: 'tab-HOUSEHOLD_ESTATE_LIFE_INSURANCE',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_LIFE_INSURANCE,
        { householdId }
      ),
    },
    {
      display: 'Liabilities',
      dataTestId: 'tab-HOUSEHOLD_ESTATE_ENTITIES_MANAGE_LIABILITIES',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_ENTITIES_MANAGE_LIABILITIES,
        { householdId }
      ),
    },
    {
      display: 'Dispositions',
      dataTestId: 'tab-HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
        { householdId }
      ),
    },
    {
      display: 'All entities',
      dataTestId: 'tab-HOUSEHOLD_ESTATE_ENTITIES',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ESTATE_ENTITIES, {
        householdId,
      }),
    },
  ]);
}
